import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { brand } from '../../theme';
import { DirectionCategoryId, getAnnotationCategory, withAlpha } from '../../utils/annotationCategories';
import { formatTimecode } from '../../utils/timecode';
import {
  MarkableLine,
  MarkableTranscript,
  MarkableWord,
  TranscriptMark,
  WordDecor,
  coverMarks,
  decorateLine,
  linePositionOfWord,
  planDirection,
  queryTerms,
  spanOfWords,
  wordMatches,
} from '../../utils/transcriptMarks';
import TranscriptSelectionBar from './TranscriptSelection';
import type { TranscriptMarkingProps } from './transcriptMarking';

/**
 * The transcript as words the creator can mark: drag across words, or click
 * the first and Shift-click the last (on a touch screen, tap the first, then
 * the last), and the bar below offers Remove, Short and Note. Removed words
 * are struck through, shorts highlighted and notes underlined; clicking a
 * mark selects it and goes there.
 *
 * From the keyboard the words are one stop: the arrow keys move between
 * words and lines, Shift with an arrow selects, R, S and N mark the
 * selection, Enter selects a word or mark, Delete deletes the selected mark
 * and Esc clears. Other keys reach the studio (Space still plays).
 */

const TC_FONT = '"JetBrains Mono", "SFMono-Regular", Menlo, monospace';
/** Room the bar takes at the bottom of the list when scrolling a word into view. */
const BAR_RESERVE_PX = 120;

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

export interface MarkableTranscriptViewProps {
  transcript: MarkableTranscript;
  /** The lines shown: all of them, or those that match the search. */
  lines: MarkableLine[];
  query: string;
  fps: number;
  /** TimelineLine.index of the line being spoken. */
  activeLine: number | null;
  marking: TranscriptMarkingProps;
  onSeek: (seconds: number) => void;
  /** Opens the studio's note composer for a stretch (the panel's onAddNote). */
  onAddNote?: (line: { start: number; end: number; text: string }) => void;
  /** The scrolling list the transcript sits in. */
  scrollRef: React.RefObject<HTMLDivElement>;
}

interface RowProps {
  line: MarkableLine;
  words: MarkableWord[];
  decor: WordDecor[];
  /** Selected words in this line (-1 when none). */
  selFrom: number;
  selTo: number;
  /** The keyboard's word, when it is in this line (-1 otherwise). */
  cursor: number;
  cursorId: string;
  active: boolean;
  query: string;
  fps: number;
  onSeekLine: (line: MarkableLine) => void;
  onAddNote?: (line: MarkableLine) => void;
}

interface Run {
  removed: boolean;
  short: boolean;
  from: number;
  to: number;
}

const MarkableRow = React.memo<RowProps>(({ line, words, decor, selFrom, selTo, cursor, cursorId, active, query, fps, onSeekLine, onAddNote }) => {
  const terms = query ? queryTerms(query) : [];
  // Neighbouring words drawn the same way share one <del> or <mark>, so the
  // strike or highlight runs through the spaces between them.
  const runs: Run[] = [];
  for (let index = line.firstWord; index <= line.lastWord; index += 1) {
    const look = decor[index - line.firstWord];
    const last = runs[runs.length - 1];
    if (last && last.removed === look.removed && last.short === look.short) last.to = index;
    else runs.push({ removed: look.removed, short: look.short, from: index, to: index });
  }

  const selected = (index: number) => index >= selFrom && index <= selTo && words[index].start !== null;
  const tinted = (index: number) => selected(index) || decor[index - line.firstWord].inSelectedMark;
  /** A space between two words tinted alike goes inside the second, so the tint runs on. */
  const spaceInside = (index: number) => tinted(index) && tinted(index - 1) && selected(index) === selected(index - 1);

  const renderWord = (index: number, leadingSpace: boolean) => {
    const word = words[index];
    const look = decor[index - line.firstWord];
    const off = word.start === null;
    const classes = ['tw'];
    if (off) classes.push('tw-off');
    else if (selected(index)) classes.push('tw-sel');
    if (index === cursor) classes.push('tw-cursor');
    if (look.noteColor) classes.push('tw-note');
    if (look.markId) classes.push('tw-marked');
    if (look.inSelectedMark) classes.push('tw-in-mark');
    if (terms.length > 0 && wordMatches(word.text, terms)) classes.push('tw-match');
    return (
      <span
        key={index}
        data-w={index}
        id={index === cursor ? cursorId : undefined}
        className={classes.join(' ')}
        style={look.noteColor ? { borderBottomColor: look.noteColor } : undefined}
        title={off ? 'Trimmed off this angle, so it isn’t on the timeline.' : undefined}
      >
        {leadingSpace ? ' ' : null}
        {look.points.map((mark) => (
          <span
            key={mark.id}
            data-mark={mark.id}
            className="tw-point"
            style={{ backgroundColor: mark.color }}
            title={`${mark.label}: ${mark.text}`}
          />
        ))}
        {word.text}
      </span>
    );
  };

  return (
    <Box
      data-line-index={line.index}
      data-active={active ? 'true' : 'false'}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        borderRadius: 2,
        borderLeft: `3px solid ${active ? brand.copper : 'transparent'}`,
        bgcolor: active ? withAlpha(brand.copper, 0.08) : 'transparent',
        pl: 1,
        pr: 0.5,
        py: 0.5,
        '&:hover .transcript-line-action, &:focus-within .transcript-line-action': { opacity: 1 },
      }}
    >
      <ButtonBase
        onClick={() => onSeekLine(line)}
        aria-current={active ? 'true' : undefined}
        // Screen readers hear each line as one stop (the words themselves aren't Tab stops).
        aria-label={`${formatTimecode(line.start, fps)}: ${line.speaker ? `${line.speaker}: ` : ''}${line.text}`}
        sx={{
          flexShrink: 0,
          mt: '3px',
          px: 0.5,
          borderRadius: 1,
          '&.Mui-focusVisible': { outline: `2px solid ${brand.copper}`, outlineOffset: 1 },
        }}
      >
        <Typography
          component="span"
          variant="caption"
          sx={{ fontFamily: TC_FONT, fontVariantNumeric: 'tabular-nums', color: active ? 'primary.dark' : 'text.secondary' }}
        >
          {formatTimecode(line.start, fps)}
        </Typography>
      </ButtonBase>
      <Typography component="div" variant="body2" sx={{ flex: 1, minWidth: 0, color: 'text.primary', lineHeight: 1.75, overflowWrap: 'anywhere' }}>
        {line.speaker && <Box component="span" data-testid="transcript-speaker" sx={{ fontWeight: 700, mr: 0.75 }}>{line.speaker}</Box>}
        {runs.map((run) => {
          const children: React.ReactNode[] = [];
          for (let index = run.from; index <= run.to; index += 1) {
            const space = index > run.from && words[index].spaceBefore;
            const inside = space && spaceInside(index);
            if (space && !inside) children.push(' ');
            children.push(renderWord(index, inside));
          }
          let node: React.ReactNode = children;
          if (run.short) node = <mark className="tw-short">{node}</mark>;
          if (run.removed) node = <del className="tw-remove">{node}</del>;
          return (
            <React.Fragment key={run.from}>
              {run.from > line.firstWord && words[run.from].spaceBefore ? ' ' : null}
              {node}
            </React.Fragment>
          );
        })}
      </Typography>
      {onAddNote && (
        <Tooltip title="Add a note on this line">
          <IconButton
            size="small"
            className="transcript-line-action"
            aria-label={`Add a note at ${formatTimecode(line.start, fps)}`}
            onClick={() => onAddNote(line)}
            sx={{ mt: 0.25, opacity: { xs: 1, md: 0 }, transition: 'opacity 120ms', color: 'text.secondary', '&:focus-visible': { opacity: 1 } }}
          >
            <NoteAddOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
});
MarkableRow.displayName = 'MarkableRow';

/** Styles for every word, set once on the list instead of on thousands of spans. */
const wordStyles = (removeColor: string, shortColor: string) => ({
  '& .tw': {
    cursor: 'text',
    borderRadius: '3px',
    paddingTop: '1px',
    paddingBottom: '1px',
    touchAction: 'pan-y',
  },
  '& .tw:hover': { backgroundColor: withAlpha(brand.copper, 0.1) },
  '& .tw-marked': { cursor: 'pointer' },
  '& .tw-note': { borderBottom: '2px dotted' },
  '& .tw-match': { backgroundColor: withAlpha(brand.copper, 0.22) },
  // Square corners, so a run of tinted words reads as one band.
  '& .tw-in-mark': { backgroundColor: withAlpha(brand.copper, 0.16), borderRadius: 0 },
  '& .tw-sel, & .tw-sel:hover': { backgroundColor: withAlpha(brand.copper, 0.32), borderRadius: 0 },
  '& .tw-off': { color: 'text.disabled', cursor: 'not-allowed' },
  '& .tw-off:hover': { backgroundColor: 'transparent' },
  '& .tw-cursor': { outline: `2px solid ${brand.copper}`, outlineOffset: '1px' },
  '& .tw-point': {
    display: 'inline-block',
    width: 7,
    height: 7,
    borderRadius: '50%',
    marginRight: '3px',
    verticalAlign: 'middle',
    cursor: 'pointer',
  },
  '& del.tw-remove': {
    color: 'text.secondary',
    textDecorationLine: 'line-through',
    textDecorationColor: removeColor,
    textDecorationThickness: '2px',
  },
  '& mark.tw-short': {
    color: 'inherit',
    backgroundColor: withAlpha(shortColor, 0.14),
    borderBottom: `2px solid ${withAlpha(shortColor, 0.75)}`,
    borderRadius: '2px',
  },
});

const MarkableTranscriptView: React.FC<MarkableTranscriptViewProps> = ({
  transcript,
  lines,
  query,
  fps,
  activeLine,
  marking,
  onSeek,
  onAddNote,
  scrollRef,
}) => {
  const { words } = transcript;
  const marks = marking.marks;
  const readOnly = Boolean(marking.readOnly) || !marking.onMark;
  const selectedMarkId = marking.selectedMarkId ?? null;
  const uid = useId();
  const cursorId = `${uid}-cursor`;
  const hintId = `${uid}-hint`;
  const rootRef = useRef<HTMLDivElement | null>(null);

  const coverage = useMemo(() => coverMarks(words, marks), [words, marks]);

  // Each line's look, reusing the last one when it has not changed, so a new
  // mark only redraws the lines it touches.
  const decorCache = useRef(new Map<number, { key: string; decor: WordDecor[] }>());
  const decorByLine = useMemo(() => {
    const next = new Map<number, { key: string; decor: WordDecor[] }>();
    transcript.lines.forEach((line) => {
      const computed = decorateLine(line, coverage, selectedMarkId);
      const previous = decorCache.current.get(line.index);
      next.set(
        line.index,
        previous && previous.key === computed.key && previous.decor.length === computed.decor.length ? previous : computed
      );
    });
    decorCache.current = next;
    return next;
  }, [transcript.lines, coverage, selectedMarkId]);

  // The selection and the keyboard's word are kept by word, not position, so
  // they stay on the same words while the transcript is still growing.
  const keyIndex = useMemo(() => {
    const map = new Map<string, number>();
    words.forEach((word) => map.set(word.key, word.index));
    return map;
  }, [words]);
  const [selectionKeys, setSelectionKeys] = useState<{ anchor: string; focus: string } | null>(null);
  const [cursorKey, setCursorKey] = useState<string | null>(null);
  const [keyboard, setKeyboard] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const anchorIndex = selectionKeys ? keyIndex.get(selectionKeys.anchor) : undefined;
  const focusIndex = selectionKeys ? keyIndex.get(selectionKeys.focus) : undefined;
  const span = !readOnly && anchorIndex !== undefined && focusIndex !== undefined ? spanOfWords(words, anchorIndex, focusIndex) : null;
  const cursorIndex = cursorKey ? keyIndex.get(cursorKey) : undefined;
  const cursor = cursorIndex === undefined ? -1 : cursorIndex;
  const selectedMark = selectedMarkId ? marks.find((mark) => mark.id === selectedMarkId) ?? null : null;

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const select = useCallback((anchor: number, focus: number) => {
    setSelectionKeys({ anchor: words[anchor].key, focus: words[focus].key });
  }, [words]);
  const clearSelection = useCallback(() => setSelectionKeys(null), []);

  // A short message ("already marked") fades after a few seconds.
  useEffect(() => {
    if (!message) return undefined;
    const timer = window.setTimeout(() => setMessage(null), 5000);
    return () => window.clearTimeout(timer);
  }, [message]);

  /** Scrolls the list (not the page) so a word is in view, clear of the bar. */
  const scrollWordIntoView = useCallback((index: number, place: 'nearest' | 'third' = 'nearest') => {
    const list = scrollRef.current;
    const element = rootRef.current?.querySelector<HTMLElement>(`[data-w="${index}"]`);
    if (!list || !element || list.clientHeight === 0) return;
    const top = element.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
    const visible = top >= list.scrollTop + 8 && top <= list.scrollTop + list.clientHeight - BAR_RESERVE_PX;
    if (visible && place === 'nearest') return;
    const target = Math.max(0, place === 'third' ? top - list.clientHeight / 3 : top - (top < list.scrollTop ? 24 : list.clientHeight / 2));
    if (typeof list.scrollTo === 'function') list.scrollTo({ top: target, behavior: 'smooth' });
    else list.scrollTop = target;
  }, [scrollRef]);

  // A mark chosen elsewhere (the notes list, the timeline) comes into view, and replaces any word selection.
  const coverageRef = useRef(coverage);
  coverageRef.current = coverage;
  useEffect(() => {
    if (!selectedMarkId) return;
    setSelectionKeys(null);
    const range = coverageRef.current.range.get(selectedMarkId);
    if (range) scrollWordIntoView(range.from, 'third');
  }, [selectedMarkId, scrollWordIntoView]);

  const markById = useCallback((id: string) => marks.find((mark) => mark.id === id) ?? null, [marks]);

  /** The mark a click on a word selects: the shortest one covering it. */
  const markAtWord = (index: number): TranscriptMark | null => {
    const covering = coverage.byWord.get(index);
    if (!covering || covering.length === 0) return null;
    return covering.reduce((best, mark) => (mark.end - mark.start < best.end - best.start ? mark : best));
  };

  /** A click (or tap, or Enter) on a word: select its mark, or the word itself, and go there. */
  const activateWord = (index: number, markId?: string | null) => {
    const word = words[index];
    if (!word || word.start === null) return;
    setCursorKey(word.key);
    const mark = markId ? markById(markId) : markAtWord(index);
    if (mark) {
      setSelectionKeys(null);
      if (marking.onSelectMark) marking.onSelectMark(mark);
      else onSeek(mark.start);
      return;
    }
    if (!readOnly) select(index, index);
    onSeek(word.start);
  };

  // ---- Pointer: drag across words, click, Shift-click, tap ------------------
  const dragRef = useRef<{ anchor: number; moved: boolean; touch: boolean; x: number; y: number; markId: string | null } | null>(null);
  const stopDragRef = useRef<(() => void) | null>(null);
  useEffect(() => () => stopDragRef.current?.(), []);

  const wordIndexOf = (target: EventTarget | null): number | null => {
    const element = target instanceof Element ? target.closest('[data-w]') : null;
    if (!element || !rootRef.current || !rootRef.current.contains(element)) return null;
    const index = Number(element.getAttribute('data-w'));
    return Number.isInteger(index) && index >= 0 && index < words.length ? index : null;
  };

  /** While dragging near the top or bottom edge, the list scrolls. */
  const autoScroll = (clientY: number) => {
    const list = scrollRef.current;
    if (!list) return;
    const rect = list.getBoundingClientRect();
    const edge = 40;
    if (clientY < rect.top + edge) list.scrollTop -= Math.ceil((rect.top + edge - clientY) / 3);
    else if (clientY > rect.bottom - edge) list.scrollTop += Math.ceil((clientY - (rect.bottom - edge)) / 3);
  };

  const moveRef = useRef<(event: PointerEvent) => void>(() => undefined);
  moveRef.current = (event: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.touch) return;
    autoScroll(event.clientY);
    const under = typeof document.elementFromPoint === 'function' ? document.elementFromPoint(event.clientX, event.clientY) : null;
    const index = wordIndexOf(under);
    if (index === null || words[index].start === null) return;
    if (index !== drag.anchor) drag.moved = true;
    if (drag.moved && !readOnly) {
      select(drag.anchor, index);
      setCursorKey(words[index].key);
    }
  };
  const upRef = useRef<() => void>(() => undefined);
  upRef.current = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag && !drag.moved) activateWord(drag.anchor, drag.markId);
  };

  const startMouseDrag = () => {
    stopDragRef.current?.();
    let stop: () => void = () => undefined;
    const move = (event: PointerEvent) => moveRef.current(event);
    const up = () => {
      stop();
      upRef.current();
    };
    stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', stop);
      stopDragRef.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', stop);
    stopDragRef.current = stop;
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const index = wordIndexOf(event.target);
    if (index === null) return;
    // Words are picked, not text: keep the browser's own selection out of it, and the keyboard here.
    event.preventDefault();
    rootRef.current?.focus({ preventScroll: true });
    setKeyboard(false);
    if (words[index].start === null) return;
    const pointElement = event.target instanceof Element ? event.target.closest('[data-mark]') : null;
    const markId = pointElement ? pointElement.getAttribute('data-mark') : null;
    const touch = event.pointerType === 'touch';
    if (!touch && event.shiftKey && !readOnly) {
      // Shift-click selects from the first selected word, or the word clicked last.
      const anchor = span && anchorIndex !== undefined ? anchorIndex : cursor;
      if (anchor >= 0 && words[anchor] && words[anchor].start !== null) {
        select(anchor, index);
        setCursorKey(words[index].key);
        return;
      }
    }
    dragRef.current = { anchor: index, moved: false, touch, x: event.clientX, y: event.clientY, markId };
    if (!touch) startMouseDrag();
  };

  // A tap is decided when the finger lifts: a finger that moved was scrolling.
  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || !drag.touch) return;
    dragRef.current = null;
    if (Math.abs(event.clientX - drag.x) > 10 || Math.abs(event.clientY - drag.y) > 10) return;
    if (!readOnly && span && anchorIndex !== undefined && !drag.markId) {
      // With words selected, a tap selects up to the tapped word; tapping the first word again starts over.
      if (drag.anchor === anchorIndex) clearSelection();
      else select(anchorIndex, drag.anchor);
      setCursorKey(words[drag.anchor].key);
      return;
    }
    activateWord(drag.anchor, drag.markId);
  };
  const handlePointerCancel = () => {
    if (dragRef.current?.touch) dragRef.current = null;
  };

  // ---- Actions ----------------------------------------------------------------
  const markSelection = async (kind: DirectionCategoryId) => {
    if (!span || readOnly || busy || marking.disabledReason || !marking.onMark) return;
    const plan = planDirection(kind, span, words, marks, coverage);
    if (plan.action === 'exists') {
      setMessage(kind === 'remove' ? 'Those words are already marked to remove.' : 'Those words are already a short.');
      setSelectionKeys(null);
      if (marking.onSelectMark) marking.onSelectMark(plan.mark);
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const saved = await marking.onMark({ kind, start: plan.start, end: plan.end, text: plan.text, replaces: plan.replaces });
      if (saved && mountedRef.current) setSelectionKeys(null);
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const noteSelection = () => {
    if (!span || readOnly || !onAddNote) return;
    onAddNote({ start: span.start, end: span.end, text: span.quote });
    setSelectionKeys(null);
  };

  const deleteSelectedMark = () => {
    if (!selectedMark || readOnly || !marking.onDeleteMark || marking.disabledReason) return;
    marking.onDeleteMark(selectedMark);
  };

  // ---- Keyboard ------------------------------------------------------------------
  /** The next word that can be marked in the lines shown, `delta` words on, or -1. */
  const stepWord = (from: number, delta: 1 | -1): number => {
    let position = linePositionOfWord(lines, from);
    if (position < 0) return lines.length > 0 ? (delta > 0 ? lines[0].firstWord : lines[lines.length - 1].lastWord) : -1;
    let next = from + delta;
    for (let guard = 0; guard < words.length + lines.length; guard += 1) {
      const line = lines[position];
      if (next < line.firstWord || next > line.lastWord) {
        position += delta;
        if (position < 0 || position >= lines.length) return -1;
        next = delta > 0 ? lines[position].firstWord : lines[position].lastWord;
      }
      if (words[next].start !== null) return next;
      next += delta;
    }
    return -1;
  };

  /** The same place in the next or previous line shown. */
  const stepLine = (from: number, delta: 1 | -1): number => {
    const position = linePositionOfWord(lines, from);
    const target = lines[position < 0 ? (delta > 0 ? 0 : lines.length - 1) : position + delta];
    if (!target) return -1;
    const offset = position < 0 ? 0 : from - lines[position].firstWord;
    const index = Math.min(target.lastWord, target.firstWord + offset);
    if (words[index].start !== null) return index;
    const after = stepWord(index, 1);
    return after >= 0 && after <= target.lastWord ? after : stepWord(index, -1);
  };

  /** Where the keyboard starts: the selection, the selected mark, the line being spoken, or the top. */
  const startingWord = (): number => {
    if (focusIndex !== undefined) return focusIndex;
    const markRange = selectedMarkId ? coverage.range.get(selectedMarkId) : undefined;
    if (markRange) return markRange.from;
    const spoken = activeLine !== null ? lines.find((line) => line.index === activeLine) : undefined;
    const first = spoken ?? lines[0];
    if (!first) return -1;
    return words[first.firstWord].start !== null ? first.firstWord : stepWord(first.firstWord, 1);
  };

  const moveCursor = (next: number, extend: boolean) => {
    if (next < 0) return;
    setCursorKey(words[next].key);
    if (extend && !readOnly) {
      const anchor = span && anchorIndex !== undefined ? anchorIndex : cursor >= 0 ? cursor : next;
      select(anchor, next);
    } else if (!extend) {
      setSelectionKeys(null);
    }
    scrollWordIntoView(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Keys on the buttons inside (line times, the bar) keep their own meaning.
    if (event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) return;
    const from = cursor >= 0 ? cursor : startingWord();
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowLeft':
        event.preventDefault();
        setKeyboard(true);
        moveCursor(cursor >= 0 ? stepWord(from, event.key === 'ArrowRight' ? 1 : -1) : from, event.shiftKey);
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        setKeyboard(true);
        moveCursor(cursor >= 0 ? stepLine(from, event.key === 'ArrowDown' ? 1 : -1) : from, event.shiftKey);
        break;
      case 'Home':
      case 'End': {
        const line = lines[linePositionOfWord(lines, from)];
        if (!line) return;
        event.preventDefault();
        setKeyboard(true);
        let target = -1;
        for (let i = line.firstWord; i <= line.lastWord; i += 1) {
          if (words[i].start === null) continue;
          if (event.key === 'End' || target < 0) target = i;
        }
        moveCursor(target, event.shiftKey);
        break;
      }
      case 'Enter':
        if (from < 0) return;
        event.preventDefault();
        setKeyboard(true);
        activateWord(from);
        break;
      case 'Escape':
        if (!span) return;
        event.preventDefault();
        clearSelection();
        break;
      case 'r':
      case 'R':
      case 's':
      case 'S':
        if (!span || readOnly) return;
        event.preventDefault();
        void markSelection(event.key.toLowerCase() === 'r' ? 'remove' : 'short');
        break;
      case 'n':
      case 'N':
        if (!span || readOnly || !onAddNote) return;
        event.preventDefault();
        noteSelection();
        break;
      case 'Delete':
      case 'Backspace':
        if (span || !selectedMark || readOnly || !marking.onDeleteMark) return;
        event.preventDefault();
        deleteSelectedMark();
        break;
      default:
        break;
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || cursor >= 0) return;
    const start = startingWord();
    if (start >= 0) setCursorKey(words[start].key);
  };

  const seekLine = useCallback((line: MarkableLine) => onSeek(line.start), [onSeek]);
  const addNoteOnLine = useCallback(
    (line: MarkableLine) => onAddNote?.({ start: line.start, end: line.end, text: line.text }),
    [onAddNote]
  );

  const removeColor = getAnnotationCategory('remove').color;
  const shortColor = getAnnotationCategory('short').color;
  const styles = useMemo(() => wordStyles(removeColor, shortColor), [removeColor, shortColor]);
  const selFrom = span ? span.from : -1;
  const selTo = span ? span.to : -1;
  const shownCursor = keyboard ? cursor : -1;

  return (
    <Box
      ref={rootRef}
      tabIndex={0}
      role="group"
      aria-label={readOnly ? 'Transcript, with the creator’s marks' : 'Transcript words'}
      aria-describedby={hintId}
      aria-activedescendant={shownCursor >= 0 ? cursorId : undefined}
      data-testid="transcript-words"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={(event) => {
        if (event.target === event.currentTarget) setKeyboard(false);
      }}
      sx={{
        position: 'relative',
        px: 1,
        pt: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        outline: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${withAlpha(brand.copper, 0.45)}`, borderRadius: 2 },
        ...styles,
      }}
    >
      <Box id={hintId} sx={visuallyHidden}>
        {readOnly
          ? 'Arrow keys move between words. Enter on a marked word selects its mark.'
          : 'Arrow keys move between words; Shift with an arrow key selects. Then press R to remove the words, S to make them a short, or N to add a note. Enter selects a word or a mark; Delete deletes the selected mark.'}
      </Box>
      {/* First in the page order, so Tab reaches its buttons before every line; drawn at the bottom. */}
      <Box sx={{ order: 1, position: 'sticky', bottom: 0, zIndex: 1 }}>
        <TranscriptSelectionBar
          fps={fps}
          readOnly={readOnly}
          disabledReason={marking.disabledReason}
          selection={span}
          selectedMark={selectedMark}
          busy={busy}
          message={message}
          onRemove={() => void markSelection('remove')}
          onShort={() => void markSelection('short')}
          onNote={onAddNote ? noteSelection : undefined}
          onClear={() => {
            setMessage(null);
            if (span) clearSelection();
            else marking.onClearSelection?.();
          }}
          onPlayMark={(mark) => onSeek(mark.start)}
          onEditMark={marking.onEditMark}
          onDeleteMark={marking.onDeleteMark ? () => deleteSelectedMark() : undefined}
        />
      </Box>
      {lines.map((line) => {
        const look = decorByLine.get(line.index);
        const inLine = (index: number) => index >= line.firstWord && index <= line.lastWord;
        const hasSelection = selFrom >= 0 && selFrom <= line.lastWord && selTo >= line.firstWord;
        return (
          <MarkableRow
            key={line.index}
            line={line}
            words={words}
            decor={look ? look.decor : []}
            selFrom={hasSelection ? selFrom : -1}
            selTo={hasSelection ? selTo : -1}
            cursor={shownCursor >= 0 && inLine(shownCursor) ? shownCursor : -1}
            cursorId={cursorId}
            active={activeLine === line.index}
            query={query}
            fps={fps}
            onSeekLine={seekLine}
            onAddNote={onAddNote && !readOnly ? addNoteOnLine : undefined}
          />
        );
      })}
    </Box>
  );
};

export default MarkableTranscriptView;
